import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Calendar, MapPin, Users, Filter, Search, TrendingUp, Crown, Clock, ChevronDown, History } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { categories, cities } from '@/lib/eventFilters';
import { fetchEvents } from '@/lib/events';
import { adaptEvents } from '@/lib/eventAdapter';
import { EventCountdown } from '@/components/EventCountdown';
import { FriendsAttending } from '@/components/FriendsAttending';
import { usePageMeta } from '@/hooks/usePageMeta';

export const Events = () => {
  usePageMeta('Events', 'Browse upcoming nightlife events — Bollywood, Punjabi, EDM and special events across New York, New Jersey and Philadelphia. Filter by city, date, genre and price.');
  const [searchParams] = useSearchParams();
  const [selectedFilter, setSelectedFilter] = useState(searchParams.get('filter') || 'all');
  const [selectedCity, setSelectedCity] = useState('all');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [priceFilter, setPriceFilter] = useState('all');
  const [showPast, setShowPast] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Backend-driven: GET /api/events returns only published (upcoming) events.
  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    fetchEvents()
      .then((data) => { if (active) setEvents(adaptEvents(data)); })
      .catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  // Filter and search logic
  const filteredEvents = events.filter(event => {
    // Search filter
    const matchesSearch = searchQuery === '' || 
      event.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.artist.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.venue.toLowerCase().includes(searchQuery.toLowerCase()) ||
      event.city.toLowerCase().includes(searchQuery.toLowerCase());

    // Category filter
    const matchesCategory = selectedFilter === 'all' || 
      (selectedFilter === 'trending' && event.trending) ||
      event.category === selectedFilter;

    // City filter
    const matchesCity = selectedCity === 'all' || event.city === selectedCity;

    // Price filter (guard null prices — never coerce missing data to 0)
    let matchesPrice = true;
    const pn = event.priceNum;
    if (priceFilter === 'under50') matchesPrice = pn != null && pn < 50;
    else if (priceFilter === '50to100') matchesPrice = pn != null && pn >= 50 && pn <= 100;
    else if (priceFilter === 'over100') matchesPrice = pn != null && pn > 100;

    return matchesSearch && matchesCategory && matchesCity && matchesPrice;
  });

  // Featured events first, then by date
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    if (a.featured && !b.featured) return -1;
    if (!a.featured && b.featured) return 1;
    return new Date(a.dateISO) - new Date(b.dateISO);
  });

  // Split into upcoming vs past based on the event date.
  // Upcoming events stay in the main grid; past events move into a
  // collapsible "Past Events" dropdown to keep the page clean.
  const now = new Date();
  const upcomingEvents = sortedEvents.filter((event) => new Date(event.dateISO) >= now);
  const pastEvents = sortedEvents
    .filter((event) => new Date(event.dateISO) < now)
    .sort((a, b) => new Date(b.dateISO) - new Date(a.dateISO));

  const renderEventCard = (event, isPast = false) => (
    <Link key={event.id} to={`/event/${event.slug}`}>
      <Card className={`group overflow-hidden border-border/50 hover-lift cursor-pointer h-full ${isPast ? 'opacity-80' : ''}`}>
        {/* Event Image */}
        <div className="relative h-56 overflow-hidden">
          <img
            src={event.image}
            alt={event.title}
            className={`w-full h-full object-cover transition-transform duration-500 group-hover:scale-110 ${isPast ? 'grayscale' : ''}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />

          {/* Badges */}
          <div className="absolute top-3 left-3 flex flex-wrap gap-2">
            {isPast ? (
              <Badge className="bg-muted text-muted-foreground backdrop-blur-sm">
                <History className="w-3 h-3 mr-1" />
                Past Event
              </Badge>
            ) : (
              <>
                {event.trending && (
                  <Badge className="bg-destructive/90 text-white backdrop-blur-sm">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Trending
                  </Badge>
                )}
                {event.tags.slice(0, 1).map((tag) => (
                  <Badge key={tag} className="bg-primary/90 text-primary-foreground backdrop-blur-sm">
                    <Crown className="w-3 h-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </>
            )}
          </div>

          {/* Capacity indicator — only when both counts are known */}
          {event.attendees != null && event.capacity != null && event.capacity > 0 && (
            <div className="absolute top-3 right-3">
              <Badge
                variant="outline"
                className={`backdrop-blur-sm ${
                  (event.attendees / event.capacity) > 0.8
                    ? 'bg-destructive/20 text-destructive border-destructive/30'
                    : 'bg-background/50'
                }`}
              >
                {Math.round((event.attendees / event.capacity) * 100)}% Full
              </Badge>
            </div>
          )}

          {/* Friends Attending */}
          {event.friendsAttending && event.friendsAttending.length > 0 && (
            <div className="absolute bottom-3 left-3">
              <FriendsAttending friendsAttending={event.friendsAttending} compact />
            </div>
          )}

          {/* Attendees — only when known */}
          {event.attendees != null && (
            <div className="absolute bottom-3 right-3 flex items-center gap-2 glass-card px-3 py-1.5 rounded-full">
              <Users className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">{event.attendees}</span>
            </div>
          )}
        </div>

        <CardContent className="p-5 space-y-3">
          <div>
            <h3 className="text-xl font-display font-bold mb-1 group-hover:text-primary transition-colors line-clamp-1">
              {event.title}
            </h3>
            <p className="text-muted-foreground text-sm">{event.artist}</p>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            <span className="truncate">{event.venue} • {event.city}</span>
          </div>

          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span>{event.date}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>{event.time}</span>
            </div>
          </div>

          {/* Countdown — only meaningful for upcoming events */}
          {!isPast && <EventCountdown dateISO={event.dateISO} eventTitle={event.title} compact />}

          <div className="pt-3 flex items-center justify-between border-t border-border/50">
            <div>
              <p className="text-xs text-muted-foreground">From</p>
              <p className="text-2xl font-bold text-primary">{event.price}</p>
            </div>
            <Button
              variant={isPast ? 'outline' : 'premium'}
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = `/event/${event.slug}`;
              }}
            >
              {isPast ? 'View Details' : 'Get Tickets'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </Link>
  );

  return (
    <div className="min-h-screen pt-20 pb-24 md:pb-8">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-display font-bold mb-3">
            Discover <span className="text-gradient-gold">Events</span>
          </h1>
          <p className="text-muted-foreground text-lg">
            Find the hottest nightlife experiences near you
          </p>
        </div>

        {/* Featured Event Hero */}
        {(() => {
          const featured = events.find(e => e.featured);
          if (!featured) return null;
          return (
            <Link to={`/event/${featured.slug}`} className="block mb-10">
              <Card className="overflow-hidden border-2 border-primary/40 bg-gradient-to-br from-orange-600/20 via-background to-background hover:border-primary transition-colors">
                <CardContent className="p-6 md:p-8">
                  <div className="grid md:grid-cols-2 gap-6 items-center">
                    <div>
                      <Badge className="bg-primary text-primary-foreground mb-3">
                        <Crown className="w-3 h-3 mr-1" />
                        Featured Event
                      </Badge>
                      <h2 className="text-3xl md:text-4xl font-display font-bold mb-2">
                        {featured.title}
                      </h2>
                      <p className="text-lg text-muted-foreground mb-4">
                        {featured.artist}
                      </p>
                      <div className="flex flex-wrap gap-4 mb-4 text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-primary" />
                          <span>{featured.date}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-primary" />
                          <span>{featured.venue} • {featured.city}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-primary" />
                          <span>{featured.time}</span>
                        </div>
                      </div>
                      <Button variant="premium" size="lg">
                        Get Tickets
                      </Button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="aspect-[3/4] rounded-xl overflow-hidden bg-black">
                        <img
                          src="/assets/events/orange-street-original.jpg"
                          alt={`${featured.title} poster`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="aspect-[3/4] rounded-xl overflow-hidden bg-black">
                        <img
                          src="/assets/events/poster-soundwolves.jpg"
                          alt={`${featured.title} — Soundwolves edition`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })()}

        {/* Search & Filters */}
        <div className="mb-8 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search events, artists, venues..."
                className="pl-10"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <Select value={selectedFilter} onValueChange={setSelectedFilter}>
              <SelectTrigger className="w-full sm:w-48">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-wrap gap-4">
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className="w-full sm:w-44">
                <MapPin className="w-4 h-4 mr-2" />
                <SelectValue placeholder="City" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city} value={city}>{city}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={priceFilter} onValueChange={setPriceFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Price" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Prices</SelectItem>
                <SelectItem value="under50">Under $50</SelectItem>
                <SelectItem value="50to100">$50 - $100</SelectItem>
                <SelectItem value="over100">Over $100</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Quick Filters */}
          <div className="flex flex-wrap gap-2">
            <Badge 
              variant={selectedFilter === 'trending' ? 'default' : 'outline'} 
              className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
              onClick={() => setSelectedFilter(selectedFilter === 'trending' ? 'all' : 'trending')}
            >
              <TrendingUp className="w-3 h-3 mr-1" />
              Trending
            </Badge>
            <Badge 
              variant="outline" 
              className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
              onClick={() => setPriceFilter(priceFilter === 'under50' ? 'all' : 'under50')}
            >
              Under $50
            </Badge>
            <Badge 
              variant="outline" 
              className="cursor-pointer hover:bg-primary hover:text-primary-foreground transition-colors"
              onClick={() => setSelectedFilter(selectedFilter === 'special' ? 'all' : 'special')}
            >
              Special Events
            </Badge>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-20 text-muted-foreground">Loading events…</div>
        ) : loadError ? (
          <div className="text-center py-20">
            <p className="text-xl text-muted-foreground mb-4">Couldn't load events. Please try again.</p>
            <Button variant="outline" onClick={() => window.location.reload()}>Retry</Button>
          </div>
        ) : (
        <>
        {/* Results Count */}
        <div className="mb-6">
          <p className="text-sm text-muted-foreground">
            Showing {upcomingEvents.length} upcoming event{upcomingEvents.length !== 1 ? 's' : ''}
            {pastEvents.length > 0 && ` • ${pastEvents.length} past`}
          </p>
        </div>

        {/* Upcoming Events Grid */}
        {upcomingEvents.length === 0 && pastEvents.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl text-muted-foreground mb-4">No events found</p>
            <p className="text-sm text-muted-foreground mb-6">Try adjusting your search or filters</p>
            <Button variant="outline" onClick={() => {
              setSearchQuery('');
              setSelectedFilter('all');
              setSelectedCity('all');
              setPriceFilter('all');
            }}>
              Clear Filters
            </Button>
          </div>
        ) : (
          <>
            {upcomingEvents.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {upcomingEvents.map((event) => renderEventCard(event, false))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-lg text-muted-foreground">No upcoming events right now — check out past events below.</p>
              </div>
            )}

            {/* Past Events — collapsible dropdown to keep the page clean */}
            {pastEvents.length > 0 && (
              <div className="mt-12 border-t border-border/50 pt-8">
                <button
                  type="button"
                  onClick={() => setShowPast((prev) => !prev)}
                  className="flex w-full items-center justify-between gap-3 rounded-xl border border-border/50 bg-card/50 px-5 py-4 text-left transition-colors hover:border-primary/50 hover:bg-card"
                  aria-expanded={showPast}
                >
                  <span className="flex items-center gap-3">
                    <History className="w-5 h-5 text-primary" />
                    <span className="text-lg font-display font-bold">
                      Past Events
                    </span>
                    <Badge variant="outline" className="bg-background/50">
                      {pastEvents.length}
                    </Badge>
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 text-muted-foreground transition-transform duration-300 ${showPast ? 'rotate-180' : ''}`}
                  />
                </button>

                {showPast && (
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {pastEvents.map((event) => renderEventCard(event, true))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
        </>
        )}
      </div>
    </div>
  );
};

export default Events;
